import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function SubscriptionSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ phase: "polling", data: null, error: null });
  const attemptsRef = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState({ phase: "error", data: null, error: "Falta session_id" });
      return;
    }
    try {
      const { data } = await api.get(`/subscriptions/stripe/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        setState({ phase: "success", data, error: null });
        return;
      }
      if (data.status === "expired") {
        setState({ phase: "expired", data, error: null });
        return;
      }
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setState({ phase: "timeout", data, error: null });
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    } catch (e) {
      setState({
        phase: "error",
        data: null,
        error: e?.response?.data?.detail?.message || "No se pudo confirmar el pago.",
      });
    }
  }, [sessionId, refresh]);

  useEffect(() => {
    attemptsRef.current = 0;
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div
      className="min-h-screen bg-[#0A0E1A] text-[#F1F5F9] flex items-center justify-center px-4"
      data-testid="subscription-success-page"
    >
      <div className="max-w-md w-full bg-[#141923] border border-[#2A3447] rounded-xl p-8 text-center">
        {state.phase === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-[#3B82F6] mx-auto animate-spin" />
            <h1 className="mt-5 text-xl font-bold">Confirmando tu pago…</h1>
            <p className="text-sm text-[#94A3B8] mt-2">
              Esto puede tardar unos segundos. No cierres esta ventana.
            </p>
          </>
        )}

        {state.phase === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#10B981] mx-auto" />
            <h1 className="mt-5 text-2xl font-bold" data-testid="subscription-success-title">
              ¡Bienvenido a Premium!
            </h1>
            <p className="text-sm text-[#94A3B8] mt-2">
              Tu suscripción está activa. Disfruta de proyecciones, recomendaciones y todo el
              poder de Finly+.
            </p>
            <Button
              className="mt-6 w-full bg-[#3B82F6] hover:bg-[#2563EB]"
              onClick={() => navigate("/dashboard")}
              data-testid="subscription-success-cta"
            >
              Ir al dashboard
            </Button>
          </>
        )}

        {state.phase === "expired" && (
          <>
            <AlertCircle className="w-12 h-12 text-[#F59E0B] mx-auto" />
            <h1 className="mt-5 text-xl font-bold">Sesión expirada</h1>
            <p className="text-sm text-[#94A3B8] mt-2">
              El checkout expiró antes de completarse. Vuelve a intentarlo desde el dashboard.
            </p>
            <Link to="/dashboard">
              <Button className="mt-6 w-full bg-[#3B82F6] hover:bg-[#2563EB]">
                Volver al dashboard
              </Button>
            </Link>
          </>
        )}

        {state.phase === "timeout" && (
          <>
            <AlertCircle className="w-12 h-12 text-[#F59E0B] mx-auto" />
            <h1 className="mt-5 text-xl font-bold">Aún procesando</h1>
            <p className="text-sm text-[#94A3B8] mt-2">
              Tu pago se está confirmando. Si en unos minutos no ves Premium activo, contacta
              soporte con el ID <code className="font-mono">{sessionId}</code>.
            </p>
            <Link to="/dashboard">
              <Button className="mt-6 w-full bg-[#3B82F6] hover:bg-[#2563EB]">
                Ir al dashboard
              </Button>
            </Link>
          </>
        )}

        {state.phase === "error" && (
          <>
            <AlertCircle className="w-12 h-12 text-[#EF4444] mx-auto" />
            <h1 className="mt-5 text-xl font-bold">No pudimos confirmar el pago</h1>
            <p className="text-sm text-[#94A3B8] mt-2">{state.error}</p>
            <Link to="/dashboard">
              <Button className="mt-6 w-full bg-[#3B82F6] hover:bg-[#2563EB]">
                Volver al dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
